// 日志级别枚举
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4
}

// 日志配置接口
interface LoggerConfig {
  level: LogLevel;
  enableTimestamp: boolean;
  enableFileInfo: boolean;
  enableColors: boolean;
  prefix?: string;
}

// 默认配置
const defaultConfig: LoggerConfig = {
  level: import.meta.env.MODE === 'production' ? LogLevel.WARN : LogLevel.DEBUG,
  enableTimestamp: true,
  enableFileInfo: true,
  enableColors: true,
  prefix: '[MIUTA]'
};

// 颜色样式
const colors = {
  debug: 'color: #6B7280; font-weight: normal;',
  info: 'color: #3B82F6; font-weight: normal;',
  warn: 'color: #F59E0B; font-weight: bold;',
  error: 'color: #EF4444; font-weight: bold;',
  timestamp: 'color: #9CA3AF; font-weight: normal;',
  file: 'color: #8B5CF6; font-weight: normal;'
};

class Logger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  // 更新配置
  updateConfig(config: Partial<LoggerConfig>) {
    this.config = { ...this.config, ...config };
  }

  // 获取当前时间戳
  private getTimestamp(): string {
    return new Date().toISOString().replace('T', ' ').slice(0, -5);
  }

  // 获取调用文件信息
  private getFileInfo(): string {
    const stack = new Error().stack;
    if (!stack) return '';
    
    const lines = stack.split('\n');
    // 跳过Logger内部调用，找到实际调用位置
    for (let i = 3; i < lines.length; i++) {
      const line = lines[i];
      if (line.includes('http://') || line.includes('https://')) {
        const match = line.match(/\/([^/]+):(\d+):(\d+)/);
        if (match) {
          return `${match[1]}:${match[2]}`;
        }
      }
    }
    return '';
  }

  // 格式化消息
  private formatMessage(level: string, message: any, ...args: any[]): [string, string[]] {
    const parts: string[] = [];
    const styles: string[] = [];

    // 添加前缀
    if (this.config.prefix) {
      parts.push(`%c${this.config.prefix}`);
      styles.push(colors[level as keyof typeof colors]);
    }

    // 添加时间戳
    if (this.config.enableTimestamp) {
      parts.push(`%c[${this.getTimestamp()}]`);
      styles.push(colors.timestamp);
    }

    // 添加级别
    parts.push(`%c[${level.toUpperCase()}]`);
    styles.push(colors[level as keyof typeof colors]);

    // 添加文件信息
    if (this.config.enableFileInfo) {
      const fileInfo = this.getFileInfo();
      if (fileInfo) {
        parts.push(`%c[${fileInfo}]`);
        styles.push(colors.file);
      }
    }

    // 添加消息
    parts.push('%c' + String(message));
    styles.push('color: inherit;');

    return [parts.join(' '), styles];
  }

  // 通用日志方法
  private log(level: LogLevel, levelName: string, message: any, ...args: any[]) {
    if (level < this.config.level) return;

    const [formattedMessage, styles] = this.formatMessage(levelName, message, ...args);

    if (this.config.enableColors && styles.length > 0) {
      console.log(formattedMessage, ...styles, ...args);
    } else {
      // 简化版本，不使用颜色
      const simpleMessage = `${this.config.prefix || ''} [${this.getTimestamp()}] [${levelName.toUpperCase()}] ${message}`;
      console.log(simpleMessage, ...args);
    }
  }

  // 调试日志
  debug(message: any, ...args: any[]) {
    this.log(LogLevel.DEBUG, 'debug', message, ...args);
  }

  // 信息日志
  info(message: any, ...args: any[]) {
    this.log(LogLevel.INFO, 'info', message, ...args);
  }

  // 警告日志
  warn(message: any, ...args: any[]) {
    this.log(LogLevel.WARN, 'warn', message, ...args);
  }

  // 错误日志
  error(message: any, ...args: any[]) {
    this.log(LogLevel.ERROR, 'error', message, ...args);
  }

  // API 请求日志
  api(method: string, url: string, data?: any, response?: any) {
    this.info(`API ${method.toUpperCase()} ${url}`, { data, response });
  }

  // 用户行为日志
  user(action: string, data?: any) {
    this.info(`User Action: ${action}`, data);
  }

  // 组件生命周期日志
  component(componentName: string, lifecycle: string, data?: any) {
    this.debug(`Component [${componentName}] ${lifecycle}`, data);
  }
}

// 创建默认实例
const logger = new Logger();

// 导出默认实例和类
export { Logger, logger };

// 便捷方法导出
export const { debug, info, warn, error, api, user, component } = logger;